/**
 * Atomic data-layer — theme-side event tracking.
 *
 * Implements the dataLayer events from the Atomic spec that originate in the
 * THEME (storefront DOM). MOST standard ecommerce (view_item, add_to_cart,
 * purchase, checkout steps, …) is emitted by the Shopify Custom Pixel and is
 * NOT handled here, to avoid double-firing — see docs/event-capture-status.md.
 *
 * EXCEPTION — view_cart, remove_from_cart, select_item ARE handled here: the
 * Custom Pixel can't emit them (opening the native cart drawer is not a Shopify
 * pixel cart_viewed; product-list clicks have no native Shopify event). The
 * pixel never fires these, so there is no double-fire.
 * Send Ecommerce data → Data Layer (GTM V4) forwards their items to GA4.
 *
 * Events here:
 *   Click:        navigation_link, footer_link, social_link,
 *                 country_switcher, currency_switcher, email_link, tel_link
 *   PDP:          product_size_selected, product_review_clicked,
 *                 product_comparison_add, product_comparison_viewed,
 *                 product_size_guide, product_view_not_in_stock,
 *                 store_locator (find-in-store drawer open, once per page)
 *   Engagement:   view_search_results (Liquid), quick_search_click,
 *                 newsletter_subscription_started,
 *                 newsletter_subscription_completed (Liquid),
 *                 product_stock_alert (back-in-stock-form.js — on the
 *                 confirmed middleware signup response),
 *                 user_data (Liquid),
 *                 video_played, video_completed (native <video> +
 *                 YouTube IFrame API for embeds + deferred-media controllers)
 *   Ecommerce:    view_cart, remove_from_cart, select_item (pixel can't emit),
 *                 add_to_wishlist (relayed from the theme wishlist toggle)
 *
 * All listeners are delegated + feature-detected, so pages missing a component
 * simply fire nothing. Each block is guarded so one failure can't break others.
 */
(() => {
  window.dataLayer = window.dataLayer || [];

  // ---------------------------------------------------------------------------
  // Debug logging — OFF by default.
  //
  // Store-wide:  Theme settings -> Analytics & Marketing -> "Data layer debug
  //              logging" (settings.datalayer_debug, passed as data-debug on
  //              this script tag in layout/theme.liquid).
  // One session: ?dl_debug=1 on any URL. ?dl_debug=0 turns it back off.
  //
  // Output is ONE line per event. No payload dumps, no per-field noise.
  //
  // Payloads are ALWAYS retained on window.__dl (last 50, silent) so you can
  // inspect after the fact without enabling logging at all:
  //     __dl
  //     __dl.filter(e => e.event === 'select_item')
  //     copy(JSON.stringify(__dl, null, 2))
  // ---------------------------------------------------------------------------
  const DL_DEBUG = (() => {
    try {
      const q = window.location.search || '';
      if (/[?&]dl_debug=1/.test(q)) { sessionStorage.setItem('dlDebug', '1'); return true; }
      if (/[?&]dl_debug=0/.test(q)) { sessionStorage.removeItem('dlDebug'); return false; }
      if (sessionStorage.getItem('dlDebug') === '1') return true;
    } catch (e) { /* storage blocked — fall through to the theme setting */ }
    const tag = document.currentScript || document.querySelector('script[src*="datalayer-tracking"]');
    return !!(tag && tag.dataset && tag.dataset.debug === 'true');
  })();

  const DL_KEEP = 50;
  window.__dl = window.__dl || [];
  const dlKeep = (event, payload) => {
    try {
      window.__dl.push({ event: event, at: new Date().toISOString().slice(11, 19), payload: payload });
      if (window.__dl.length > DL_KEEP) window.__dl.shift();
    } catch (e) { /* never break tracking over bookkeeping */ }
  };

  const dlLog = (event, payload) => {
    dlKeep(event, payload);
    if (!DL_DEBUG || typeof console === 'undefined') return;
    try {
      const items = payload && payload.items;
      const bits = [];
      if (items && items.length) bits.push(items.length + ' item' + (items.length === 1 ? '' : 's'));
      const list = payload && (payload.item_list_id || payload.item_list_name);
      if (list) bits.push(String(list));
      const first = (items && items[0]) || {};
      const label = first.item_name || first.promotion_name;
      if (label) bits.push(label);
      console.log('%c▸ ' + event + '%c' + (bits.length ? '  ' + bits.join(' · ') : ''),
        'color:#0a7;font-weight:700', 'color:#888');
    } catch (e) { /* logging must never break tracking */ }
  };

  const dl = (obj) => {
    if (obj && obj.event) dlLog(obj.event, null);
    return window.dataLayer.push(obj);
  };
  // GA4 ecommerce events: clear prior ecommerce object first so GA4 won't merge.
  const dlEcom = (event, ecommerce) => {
    dlLog(event, ecommerce);
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ecommerce });
  };
  const txt = (el) => (el && el.textContent ? el.textContent.trim().replace(/\s+/g, ' ') : '');
  const guard = (fn) => { try { fn(); } catch (e) { /* never break the page */ } };

  const SOCIAL_HOSTS = [
    'facebook.com', 'instagram.com', 'youtube.com', 'youtu.be', 'tiktok.com',
    'twitter.com', 'x.com', 'pinterest.', 'snapchat.com', 'linkedin.com',
    'vimeo.com', 'tumblr.com',
  ];
  const isSocial = (href) => !!href && SOCIAL_HOSTS.some((h) => href.includes(h));

  // ---------------------------------------------------------------------------
  // PDP product-item helpers (data from snippets/datalayer-product-item.liquid)
  // ---------------------------------------------------------------------------
  const baseDL = () => window.atomicProductDL || null;

  // Build the GA4 item for the currently-selected variant, refining the base
  // item with the live variant id from the product form when available.
  const currentItem = () => {
    const data = baseDL();
    if (!data) return null;
    const item = Object.assign({}, data.item);
    const idEl = document.querySelector(
      'product-info input[name="id"], product-info select[name="id"], form[id^="product-form-"] [name="id"]'
    );
    const vid = idEl ? Number(idEl.value) : null;
    if (vid && Array.isArray(data.variants)) {
      const v = data.variants.find((x) => Number(x.id) === vid);
      if (v) {
        item.item_id = v.sku || item.item_id;
        // item_variant = SIZE only (brief §1.2) from the PIM size code mirrored
        // into variants[]; falls back to the full title only when absent.
        item.item_variant = v.sizeCode
          || ((v.title && v.title !== 'Default Title') ? v.title : null);
        item.price = v.price;
        // item_category5 (discount state) and item_category6 (colour) are
        // variant-scoped — refresh both from the variants[] mirror emitted by
        // snippets/datalayer-product-item.liquid.
        if (v.discountState) item.item_category5 = v.discountState;
        if (v.color) item.item_category6 = v.color;
      }
    }
    return item;
  };
  const currentEcom = () => {
    const data = baseDL();
    const item = currentItem();
    if (!data || !item) return null;
    return { currency: data.currency, value: item.price, items: [item] };
  };

  // ---------------------------------------------------------------------------
  // Cart helpers — for view_cart / remove_from_cart. The native cart drawer
  // is not a Shopify pixel cart_viewed, so we read the Shopify cart via
  // /cart.js and build GA4 items from its lines. Prices are in CENTS → /100
  // (the Custom Pixel's inflated-price bug does NOT apply to this path).
  // ---------------------------------------------------------------------------
  const ga4ItemFromCartLine = (li) => ({
    item_id: li.sku || String(li.product_id),
    item_name: li.product_title || li.title || '',
    // Strip the feed's trailing "-<season>" suffix (e.g. "ATOMIC-202600") so
    // item_brand matches the bare brand emitted by every other path ("ATOMIC").
    item_brand: (li.vendor || '').replace(/-\d{6}$/, ''),
    item_category: li.product_type || '',
    // item_variant = full readable variant title ("Teal / Black / 110"), matching
    // the PDP/card/pixel payloads; null for single-variant products.
    item_variant: (li.variant_title && li.variant_title !== 'Default Title') ? li.variant_title : null,
    // Price actually charged per unit: final_price is post-discount, price is not.
    // Regios automatic discounts only show up in final_price, so reporting `price`
    // overstated revenue on every discounted line.
    price: (typeof li.final_price === 'number') ? li.final_price / 100
      : (typeof li.discounted_price === 'number') ? li.discounted_price / 100
      : (typeof li.price === 'number') ? li.price / 100 : undefined,
    quantity: li.quantity,
  });
  // view_cart items: enriched as well, so the cart view carries the same fields as
  // add_to_cart. enrichedItemFromCartLine is defined below — this is only called
  // from event handlers, long after both exist.
  const cartToEcom = (cart) => {
    const items = (cart.items || []).map((li) => enrichedItemFromCartLine(li, cart));
    // value = Σ price × quantity, NOT cart.total_price. A cart-level discount
    // CODE (e.g. "WELCOME 10%") lands in cart_level_discount_applications and
    // lowers total_price while leaving every line's final_price at list — so using
    // total_price made value disagree with the items in the same event
    // (1187.99 vs 2 × 659.99). Codes belong on begin_checkout / purchase as
    // `coupon`, not folded into an item-derived value here.
    const value = items.reduce((s, i) => s + (i.price || 0) * (i.quantity || 0), 0);
    const ecom = {
      currency: cart.currency,
      value: Math.round(value * 100) / 100,
      items: items,
    };
    // coupon — an ORDER-level discount code entered by the shopper (e.g.
    // "WELCOME"). Shopify puts it in cart_level_discount_applications with
    // type "discount_code"; it lowers total_price but touches no line price, so it
    // is reported as a coupon rather than folded into value or item_category5.
    // AUTOMATIC applications are excluded, matching the pixel's couponOf().
    const code = (cart.cart_level_discount_applications || [])
      .find((d) => d && d.title && d.type !== 'automatic');
    if (code) ecom.coupon = code.title;
    return ecom;
  };
  const fetchCartJson = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  // ---------------------------------------------------------------------------
  // PIM enrichment for cart lines. /cart.js carries no metafields, so the bare
  // ga4ItemFromCartLine above has no item_brand (franchise) and no
  // item_category2..8. Recover them from payloads Liquid already rendered on this
  // page — snippets/datalayer-product-item.liquid (PDP) and the data-gtm-item
  // attributes from snippets/card-product.liquid (cards) — matching by variant id
  // first, then SKU. Falls back to the bare line when the product was never on
  // screen (e.g. an item added from another tab).
  // ---------------------------------------------------------------------------
  const cardItemBySku = (sku) => {
    if (!sku) return null;
    const nodes = document.querySelectorAll('[data-gtm-item]');
    for (let i = 0; i < nodes.length; i += 1) {
      try {
        const parsed = JSON.parse(nodes[i].getAttribute('data-gtm-item'));
        if (parsed && parsed.item_id === sku) return parsed;
      } catch (e) { /* malformed attr — skip */ }
    }
    return null;
  };
  // Match by PRODUCT id, not SKU: a card's data-gtm-item holds the SKU of its
  // DEFAULT variant, so a quick-add of any other size never matches by SKU and
  // the line would fall back to the bare /cart.js fields (vendor as brand, no
  // categories). Cards carry data-regios-dopp-generic-product-id, so the product
  // id is available even when the added size differs.
  // Painted Regios prices seen so far, keyed by product id. The paint is async and
  // can land after an add-to-cart, so a lookup at add time may find the price cell
  // still holding the list price. Recording what we saw while building view_item /
  // view_item_list means a later add still reports the discounted figure.
  const paintedByProduct = {};
  const rememberPainted = (productId, state) => {
    if (!productId || !state || state.price == null) return;
    paintedByProduct[String(productId)] = state;
  };
  const productIdOfNode = (el) => {
    const root = el && el.closest && el.closest('[data-regios-dopp-generic-product-id]');
    return root ? root.getAttribute('data-regios-dopp-generic-product-id') : null;
  };

  const cardNodeByProductId = (productId) => {
    if (!productId) return null;
    const roots = document.querySelectorAll('[data-regios-dopp-generic-product-id]');
    for (let i = 0; i < roots.length; i += 1) {
      if (String(roots[i].getAttribute('data-regios-dopp-generic-product-id')) !== String(productId)) continue;
      const node = roots[i].querySelector('[data-gtm-item]')
        || (roots[i].hasAttribute('data-gtm-item') ? roots[i] : null);
      if (node) return node;
    }
    return null;
  };
  const parseCardNode = (node) => {
    if (!node) return null;
    try {
      return JSON.parse(node.getAttribute('data-gtm-item'));
    } catch (e) { return null; }
  };
  // Categories carried on the cart line itself by snippets/datalayer-item-props
  // (hidden properties[_item_*]). Present for anything added through a form that
  // renders that snippet — and unlike the DOM lookups, they survive a page change.
  const itemFieldsFromLineProps = (li) => {
    const p = li && li.properties;
    if (!p) return null;
    const out = {};
    if (p._item_brand) out.item_brand = p._item_brand;
    if (p._item_variant) out.item_variant = p._item_variant;
    ['2', '3', '4', '6', '7', '8'].forEach((n) => {
      if (p['_item_category' + n]) out['item_category' + n] = p['_item_category' + n];
    });
    return Object.keys(out).length ? out : null;
  };
  // Size for a cart line when no PIM size code is reachable: the last option
  // value is the size in this catalogue's option order (colour options come
  // first), which beats reporting the whole "Black / Black / 186" string.
  const sizeFromCartLine = (li) => {
    const opts = li && li.options_with_values;
    if (Array.isArray(opts) && opts.length) {
      const last = opts[opts.length - 1];
      if (last && last.value) return String(last.value);
    }
    return null;
  };
  // Discount state from a /cart.js line — EXACT, unlike the PDP's paint-watching:
  // Regios applies real Shopify automatic discounts, so a discounted line carries
  // line_level_discount_allocations (or its price is below original_price).
  // Per-unit price actually payable for a cart line, INCLUDING an order-level
  // discount code. Shopify only allocates such a code at cart level
  // (cart_level_discount_applications); the line keeps price === final_price, so
  // line-level fields alone report the list price. Two sources, in order:
  //   1. the line totals — whichever of line_price / final_line_price /
  //      original_line_price is lowest is the payable one (their meanings are not
  //      consistent across Shopify versions: this store returns line_price BELOW
  //      final_line_price when an order discount applies);
  //   2. failing that, distribute cart.total_discount pro-rata over the subtotal.
  const cartLinePaidPerUnit = (li, cart) => {
    const qty = li.quantity || 1;
    const listUnit = (typeof li.price === 'number') ? li.price / 100 : undefined;
    const totals = [li.final_line_price, li.line_price, li.original_line_price]
      .filter((n) => typeof n === 'number' && n > 0);
    let perUnit = totals.length ? (Math.min.apply(null, totals) / qty) / 100 : listUnit;
    if (listUnit != null && perUnit != null && Math.abs(perUnit - listUnit) < 0.005
        && cart && typeof cart.total_discount === 'number' && cart.total_discount > 0
        && typeof cart.items_subtotal_price === 'number' && cart.items_subtotal_price > 0) {
      const factor = 1 - (cart.total_discount / cart.items_subtotal_price);
      perUnit = listUnit * factor;
    }
    return (perUnit == null) ? undefined : Math.round(perUnit * 100) / 100;
  };

  const cartLineDiscountState = (li) => {
    const allocs = li.line_level_discount_allocations || li.discounts || [];
    const allocated = allocs.reduce((s, a) => s + ((a && (a.amount || (a.discount_application && a.discount_application.value))) || 0), 0);
    if (allocated > 0) return 'discounted';
    if (typeof li.original_price === 'number' && typeof li.final_price === 'number'
        && li.final_price < li.original_price) return 'discounted';
    if (typeof li.compare_at_price === 'number' && typeof li.price === 'number'
        && li.compare_at_price > li.price) return 'discounted';
    return 'full_price';
  };

  const enrichedItemFromCartLine = (li, cart) => {
    const bare = ga4ItemFromCartLine(li);
    const data = baseDL();
    // Order-level discount: overrides everything below, because it is the only
    // source that knows what the shopper actually pays for this line.
    const paidUnit = cartLinePaidPerUnit(li, cart);
    const listUnit = (typeof li.price === 'number') ? li.price / 100 : undefined;
    const orderDiscounted = (paidUnit != null && listUnit != null && paidUnit < listUnit - 0.005);
    // The cart line knows the real discount state — it beats both the Liquid
    // compare_at snapshot and the PDP paint heuristic.
    const cartState = cartLineDiscountState(li);
    // Price actually charged per unit, discounts included. final_price is the
    // per-unit price after line-level discounts (cents); price is the
    // pre-discount one, so prefer final_price and never report variant.price here.
    const paid = (typeof li.final_price === 'number') ? li.final_price / 100
      : (typeof li.discounted_price === 'number') ? li.discounted_price / 100
      : (typeof li.price === 'number') ? li.price / 100 : undefined;
    // PDP payload: match the cart line's variant against variants[].
    if (data && Array.isArray(data.variants)) {
      const v = data.variants.find((x) => Number(x.id) === Number(li.variant_id));
      if (v) {
        const item = Object.assign({}, data.item, {
          item_id: v.sku || bare.item_id,
          item_variant: v.sizeCode
            || ((v.title && v.title !== 'Default Title') ? v.title : null),
          price: (paid != null) ? paid : v.price,
          quantity: li.quantity,
          item_category5: cartState,
        });
        if (v.color) item.item_category6 = v.color;
        // Same discount-function caveat as the card branch: /cart.js cannot see a
        // Regios discount, so prefer the price painted on this PDP.
        const pdpSale = domSalePrice();
        if (pdpSale != null) {
          item.price = pdpSale;
          item.item_category5 = 'discounted';
        }
        // An order-level code beats both — it is what will actually be charged.
        if (orderDiscounted) {
          item.price = paidUnit;
          item.item_category5 = 'discounted';
        }
        return item;
      }
    }
    // Card payload (PLP / upsell tiles): product id first (survives a quick-add
    // of a non-default size), then SKU.
    const cardNode = cardNodeByProductId(li.product_id);
    const card = parseCardNode(cardNode) || cardItemBySku(li.sku);
    if (card) {
      // Regios discounts are Shopify discount FUNCTIONS, applied at checkout, so
      // /cart.js reports neither the discounted price nor any allocation. When the
      // line came from a card that is painted as discounted, that painted price is
      // the only pre-checkout truth available.
      let painted = cardNode ? cardPriceState(cardNode) : {};
      // Nothing painted right now → fall back to what we saw earlier on this page.
      if (painted.price == null && paintedByProduct[String(li.product_id)]) {
        painted = paintedByProduct[String(li.product_id)];
      }
      const orderOverride = orderDiscounted
        ? { price: paidUnit, item_category5: 'discounted' } : {};
      return Object.assign({}, card, {
        // The card's payload describes its DEFAULT variant — override everything
        // variant-scoped with the line's own values.
        item_id: li.sku || card.item_id,
        item_variant: sizeFromCartLine(li) || card.item_variant,
        quantity: li.quantity,
        item_category5: cartState,
        price: (paid != null) ? paid : card.price,
      }, painted, itemFieldsFromLineProps(li) || {}, orderOverride);
    }
    // Last resort: bare /cart.js fields, plus whatever the line carries as props.
    return Object.assign(bare, {
      item_category5: cartState,
      item_variant: sizeFromCartLine(li) || bare.item_variant,
      price: (paid != null) ? paid : bare.price,
    }, itemFieldsFromLineProps(li) || {},
    orderDiscounted ? { price: paidUnit, item_category5: 'discounted' } : {});
  };

  // ---------------------------------------------------------------------------
  // CLICK EVENTS — single delegated listener
  // ---------------------------------------------------------------------------
  guard(() => {
    document.addEventListener('click', (event) => {
      // Predictive/quick search results are handled by quick_search_click —
      // skip here so a result click doesn't also fire navigation_link.
      if (event.target.closest('predictive-search, #predictive-search-results, .predictive-search')) return;

      // Country/currency switcher options are <span> elements, so they must be
      // checked before the anchor/button guard below (which would otherwise
      // return early and make these branches unreachable).
      const country = event.target.closest(
        '.header-localization__country, [data-country-code], [data-international]'
      );
      if (country && !country.closest('[data-currency-code], .header-localization__currency')) {
        dl({
          event: 'country_switcher',
          link_text: txt(country) || country.getAttribute('data-country-code') || '',
          click_url: country.getAttribute('data-country-code') || country.getAttribute('href') || '',
        });
        return;
      }

      // Currency switcher (if a distinct currency control exists)
      const currency = event.target.closest('[data-currency-code], .header-localization__currency');
      if (currency) {
        dl({
          event: 'currency_switcher',
          link_text: txt(currency) || currency.getAttribute('data-currency-code') || '',
          click_url: currency.getAttribute('data-currency-code') || currency.getAttribute('href') || '',
        });
        return;
      }

      const a = event.target.closest('a[href], button');
      if (!a) return;

      if (a.tagName !== 'A') return;
      const href = a.getAttribute('href') || '';

      // Email links — sitewide, wherever the link lives (footer, contact page,
      // rich text, …). Fires INSTEAD of footer_link/navigation_link so a footer
      // mailto is counted once, as an email click.
      if (href.startsWith('mailto:')) {
        dl({
          // Brief §5.6 names this event `email_link`. It shipped as
          // `email_click`, so a GTM trigger built to the document never matched.
          event: 'email_link',
          link_text: txt(a) || a.getAttribute('aria-label') || '',
          click_url: href,
          // strip "mailto:" and any ?subject=… query
          email_address: href.slice(7).split('?')[0].trim(),
        });
        return;
      }
      // Telephone links — tracked separately so they don't also fire as
      // footer_link/navigation_link when placed in those containers.
      if (href.startsWith('tel:')) {
        dl({
          event: 'tel_link',
          link_text: txt(a) || a.getAttribute('aria-label') || '',
          click_url: href,
        });
        return;
      }

      // Social links (by container or by destination host)
      if (a.closest('.list-social, .social-icons, [class*="social"]') || isSocial(a.href)) {
        dl({ event: 'social_link', link_text: txt(a) || a.getAttribute('aria-label') || '', click_url: a.href });
        return;
      }

      // Footer links
      if (a.closest('footer, .footer, .section-footer, [class*="footer"]')) {
        dl({ event: 'footer_link', link_text: txt(a), click_url: a.href });
        return;
      }

      // Main navigation links (3 levels)
      const navScope = a.closest(
        'header, .header, .section-header, sticky-header, .header__inline-menu, .atomic-mega, nav-drawer, .nav-drawer, nav'
      );
      if (navScope) {
        // The cart icon is an <a> in the header, but clicking it opens the cart
        // (tracked as view_cart elsewhere) — don't also count it as navigation.
        if (a.closest('#cart-icon-bubble, [id^="cart-icon-bubble"], [class*="header__icon--cart"]')) return;
        let level = 'level_1';
        // The mobile drawer has ONE "Shop all" in its header, and theme.js
        // rewrites its href from the active panel's data-url each time a panel
        // opens. Its level is therefore the ACTIVE PANEL'S level minus one —
        // the panel lists the children, the link goes to their parent:
        //   panel data-level="2" (Muži's children) -> Shop all = Muži  -> level_1
        //   panel data-level="3" (Lyže's children) -> Shop all = Lyže  -> level_2
        // A constant would be wrong at every depth but one, and the generic
        // [data-level] lookup below reports the panel's own level, one too deep.
        const isShopAll = a.classList.contains('nav-drawer__shop-all') || !!a.closest('.nav-drawer__shop-all');
        if (isShopAll) {
          const panel = document.querySelector('.nav-drawer__panel.is-active[data-level]');
          const depth = panel ? parseInt(panel.getAttribute('data-level'), 10) : NaN;
          const parent = isNaN(depth) ? 1 : Math.min(Math.max(depth - 1, 1), 3);
          level = 'level_' + parent;
        }
        else if (a.closest('.atomic-mega__column-link, [data-level="3"]')) level = 'level_3';
        else if (a.closest('.atomic-mega__column-title, [data-level="2"]')) level = 'level_2';
        else if (a.closest('.header__menu-item, [data-level="1"]')) level = 'level_1';
        dl({ event: 'navigation_link', link_text: txt(a), click_url: a.href, navigation_level: level });
        return;
      }
    }, true);
  });

  // ---------------------------------------------------------------------------
  // PDP — product_size_selected (variant/size change)
  // ---------------------------------------------------------------------------
  guard(() => {
    const sizeName = () => (baseDL() && baseDL().sizeOptionName ? baseDL().sizeOptionName.toLowerCase() : 'size');
    // AAS-4896 — de-dup: a PDP carries the visible size radios AND the hidden
    // options[<Size>] select, so one selection can reach us from both.
    let lastKey = '';
    let lastAt = 0;
    const fireSize = (eventName) => {
      // read variant id after the theme updates it
      setTimeout(() => guard(() => {
        const ec = currentEcom();
        if (!ec) return;
        const item = (ec.items && ec.items[0]) || {};
        const key = eventName + '|' + String(item.item_id) + '|' + String(item.item_variant);
        const now = Date.now();
        if (key === lastKey && now - lastAt < 800) return;
        lastKey = key;
        lastAt = now;
        dlEcom(eventName, ec);
      }), 0);
    };

    // Selects / radios inside the variant picker. Covers the mobile bottom sheet
    // too — it selects via selectMainOption() (mobile-option-sheets.js), which
    // dispatches `change` on the hidden select. AAS-4896: a second click listener
    // on [data-sheet-option-value] here made every sheet tap fire twice.
    document.addEventListener('change', (event) => {
      const t = event.target;
      if (!t || !t.name) return;
      if (!t.closest('variant-selects')) return;
      // Variant inputs may be named options[<OptionName>] (the hidden selects)
      // or just <OptionName> (the visible size radios) — handle both so a size
      // change fires regardless of which control the shopper actually used.
      const optName = (t.name.startsWith('options[') && t.name.endsWith(']'))
        ? t.name.slice(8, -1).toLowerCase()
        : t.name.toLowerCase();
      if (!optName) return;
      // Size AND colour both fire product_size_selected: picking either one
      // picks a variant, and a variant is colour x size. One selection, one
      // event. currentEcom() re-reads the live variant, so the payload already
      // reflects whichever axis just changed.
      if (optName === sizeName() || optionNameSets().colours.indexOf(optName) > -1) {
        fireSize('product_size_selected');
      }
    });
  });

  // ---------------------------------------------------------------------------
  // product_size_selected (§3.1) OFF the PDP — cards, quick-add drawer, wishlist.
  //
  // The block above only works on a PDP: it reads window.atomicProductDL and
  // requires a <variant-selects> element. A shopper can also change size on a
  // product card, in the quick-add drawer the card opens, and in the wishlist —
  // and the brief says "Click on size change within product", with no page
  // restriction. None of those fired.
  //
  // The item payload comes from the card's own data-gtm-item, so these events
  // carry the same fields as select_item, with item_variant swapped for the size
  // actually chosen.
  // ---------------------------------------------------------------------------
  // Theme-wide colour option names (settings.matching_option_names), exposed on
  // the quick-add drawer. Falls back to the common localised spellings so a
  // page without the drawer still classifies correctly.
  const optionNameSets = () => {
    const el = document.querySelector('[data-color-options]');
    const raw = el ? (el.getAttribute('data-color-options') || '') : '';
    const colours = raw.split(',').map((v) => v.trim().toLowerCase()).filter(Boolean);
    if (!colours.length) colours.push('color', 'colour', 'barva', 'farbe', 'couleur');
    return {
      colours,
      sizes: ['size', 'velikost', 'größe', 'grosse', 'taille', 'rozmiar'],
    };
  };

  guard(() => {
    // The quick-add drawer is rendered outside the card it belongs to, so
    // remember which card opened it.
    let lastCard = null;
    document.addEventListener('click', (event) => {
      if (!event.target || !event.target.closest) return;
      const trigger = event.target.closest('[data-quick-add-trigger]');
      if (trigger) lastCard = trigger.closest('[data-regios-dopp-generic-product-id]') || null;
    }, true);

    const payloadNear = (el) => {
      const scope = el.closest('[data-regios-dopp-generic-product-id]') ||
        el.closest('.wk-product-card, wishlist-product-card') || null;
      const holder = (scope && scope.querySelector('[data-gtm-item]')) ||
        (lastCard && lastCard.querySelector('[data-gtm-item]')) || null;
      if (!holder) return null;
      try { return JSON.parse(holder.getAttribute('data-gtm-item')); } catch (e) { return null; }
    };

    let lastKey = '';
    let lastAt = 0;
    const fire = (el, value, axis) => guard(() => {
      const item = payloadNear(el);
      if (!item) return;
      const next = Object.assign({}, item);
      // Reflect the chosen value on the axis it belongs to: size lands in
      // item_variant, colour in item_category6 (the colour dimension).
      if (value && axis === 'size') next.item_variant = value;
      if (value && axis === 'colour') next.item_category6 = value;
      // Same de-dup shape as the PDP path: one selection can arrive from both a
      // visible control and a hidden input.
      const key = String(next.item_id) + '|' + String(value);
      const now = Date.now();
      if (key === lastKey && now - lastAt < 800) return;
      lastKey = key;
      lastAt = now;
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined;
      dlEcom('product_size_selected', { currency: currency, items: [next] });
    });

    // Card size chips (PLP cards and any app card reusing the same markup).
    document.addEventListener('click', (event) => {
      if (!event.target || !event.target.closest) return;
      if (event.target.closest('variant-selects')) return; // PDP handler owns that
      const chip = event.target.closest('[data-size-value]');
      if (chip) { fire(chip, chip.getAttribute('data-size-value'), 'size'); return; }
      // Colour swatches on a card (snippets/card-product-swatch.liquid) and the
      // ones the quick-add drawer clones from them.
      const swatch = event.target.closest('.swatch-button[data-option-value]');
      if (swatch) fire(swatch, swatch.getAttribute('data-option-value'), 'colour');
    });

    // Quick-add drawer radios (assets/quick-add-drawer.js builds these with
    // data-option-name / data-option-value and dispatches `change`).
    document.addEventListener('change', (event) => {
      const t = event.target;
      if (!t || !t.getAttribute) return;
      if (t.closest && t.closest('variant-selects')) return;
      // Two shapes reach here:
      //   1. the quick-add drawer's radios — data-option-name/-value
      //   2. Wishlist King's <select name="options[Color]"> — the PDP naming,
      //      but NOT wrapped in <variant-selects>, so the PDP handler ignores
      //      it and nothing tracked wishlist variant changes at all.
      let optName = t.getAttribute('data-option-name');
      let optValue = t.getAttribute('data-option-value');
      if (!optName) {
        const nm = t.getAttribute('name') || '';
        if (nm.indexOf('options[') === 0 && nm.charAt(nm.length - 1) === ']') {
          optName = nm.slice(8, -1);
          optValue = t.value;
        }
      }
      if (!optName || !optValue) return;
      const names = optionNameSets();
      const key = String(optName).toLowerCase();
      if (names.sizes.indexOf(key) > -1) { fire(t, optValue, 'size'); return; }
      if (names.colours.indexOf(key) > -1) fire(t, optValue, 'colour');
    });
  });

  // ---------------------------------------------------------------------------
  // PDP — clicks: review, comparison add, size guide
  // ---------------------------------------------------------------------------
  guard(() => {
    document.addEventListener('click', (event) => {
      // Size guide
      // [data-sticky-open-sizeguide] is the sticky CTA bar's size-guide trigger
      // (snippets/pdp-sticky-ctas-bar.liquid). It was missing here, so on PDPs
      // where the sticky bar is the only entry point — skis, for one —
      // product_size_guide could never fire. Its [data-sticky-open-findsize]
      // twin dropped out of this selector when the "Find your size" mockup was
      // removed in AAS-5265 — no such trigger exists in the theme any more.
      // [data-drawer-open^="Sizeguide"] replaced [data-modal*="Sizeguide"] when the
      // size-guide modal moved onto the real info-drawer shell (AAS-5265) — the
      // mobile option sheet's "Size guide" link matches ONLY through it (no
      // ProductPopup- id there). The legacy data-modal selector stays for safety.
      if (event.target.closest(
        '[data-modal*="Sizeguide"], [data-drawer-open^="Sizeguide"], [id^="ProductPopup-"], .product-popup-modal__button, [data-sticky-open-sizeguide]'
      )) {
        const ec = currentEcom();
        if (ec) dlEcom('product_size_guide', ec);
        return;
      }
      // Reviews
      if (event.target.closest(
        '[data-open-reviews-modal], .pdp-extra-grid__cell--rating, [data-modal*="Reviews"], [aria-controls*="Reviews"], reviews-drawer a, .pdp-drawer__reviews-content a, a[href*="reviews"], .jdgm-prev-badge'
      )) {
        const ec = currentEcom();
        if (ec) dlEcom('product_review_clicked', ec);
        return;
      }
    }, true); // capture phase — the reviews drawer stops propagation before bubble listeners run
  });

  // ---------------------------------------------------------------------------
  // Compare — product_comparison_add / product_comparison_viewed
  // Driven by CustomEvents from assets/section-pdp-compare.js (which holds the
  // real product data); a DOM scan can't see the JS-hydrated comparison.
  // AAS-1069: compare is gated behind the "Enable product compare" theme
  // setting; when off, section-pdp-compare.js is not loaded and these listeners
  // stay dormant (they simply never fire).
  // ---------------------------------------------------------------------------
  guard(() => {
    window.addEventListener('atomic:compare:add', (e) => guard(() => {
      const item = e.detail && e.detail.item;
      if (item) dlEcom('product_comparison_add', { currency: e.detail.currency, value: item.price, items: [item] });
    }));
    window.addEventListener('atomic:compare:viewed', (e) => guard(() => {
      const items = e.detail && e.detail.items;
      // value = first compared product's price (spec: single value field)
      if (items && items.length >= 2) dlEcom('product_comparison_viewed', { currency: e.detail.currency, value: items[0].price, items: items });
    }));
  });

  // ---------------------------------------------------------------------------
  // PDP — page-load: product_view_not_in_stock
  // ---------------------------------------------------------------------------
  guard(() => {
    const data = baseDL();
    if (data && data.available === false) {
      const ec = currentEcom();
      if (ec) dlEcom('product_view_not_in_stock', ec);
    }
  });

  // ---------------------------------------------------------------------------
  // Quick (predictive) search — quick_search_click
  // ---------------------------------------------------------------------------
  guard(() => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest(
        '#predictive-search-results a, .predictive-search__list-item a, predictive-search a[href]'
      );
      if (!link) return;
      const input = document.querySelector('predictive-search input[type="search"], input[type="search"][name="q"]');
      const li = link.closest('[role="option"], li');
      let pos = null;
      if (li && li.parentElement) {
        pos = Array.prototype.indexOf.call(li.parentElement.querySelectorAll('[role="option"], li'), li) + 1 || null;
      }
      // Prefer the product/result title over the whole card text (price, etc.)
      const heading = link.querySelector(
        '.predictive-search__item-heading, .predictive-search__item-query-result, h3, h4'
      );
      dl({
        event: 'quick_search_click',
        search_term: input ? input.value.trim() : '',
        clicked_result: heading ? txt(heading) : txt(link),
        clicked_position: pos,
      });
    });
  });

  // ---------------------------------------------------------------------------
  // view_search_results (§4.5) for PREDICTIVE (mini) search.
  //
  // The full /search page emits this from inline Liquid, where the count is
  // known server-side. The mini search renders into a dropdown with no page
  // load, so nothing emitted it at all.
  //
  // Debounced and deduped per search term: the dropdown re-renders on every
  // keystroke, and reporting "red", "reds", "redst"… as separate searches would
  // be noise. Only the settled term, once, with the count actually shown.
  // ---------------------------------------------------------------------------
  guard(() => {
    const seenTerms = new Set();
    let timer = null;

    const report = () => guard(() => {
      const results = document.querySelector('#predictive-search-results, predictive-search [role="listbox"], .predictive-search__results');
      if (!results || !results.offsetParent) return;
      const input = document.querySelector('predictive-search input[type="search"], input[type="search"][name="q"]');
      const term = input ? input.value.trim() : '';
      if (!term || seenTerms.has(term)) return;
      // Count the product results actually rendered in the dropdown.
      const count = results.querySelectorAll('#predictive-search-results-products-list [role="option"], .predictive-search__list-item').length;
      if (!count) return;
      seenTerms.add(term);
      dl({ event: 'view_search_results', search_term: term, results_count: count });
    });

    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(report, 600);
    };

    document.addEventListener('input', (event) => {
      if (!event.target || !event.target.closest) return;
      if (!event.target.closest('predictive-search, [id*="predictive"]')) return;
      schedule();
    }, true);

    // The dropdown is fetched, so the DOM lands after the keystroke settles.
    guard(() => {
      const host = document.querySelector('predictive-search');
      if (!host) return;
      const obs = new MutationObserver(schedule);
      obs.observe(host, { childList: true, subtree: true });
    });
  });

  // ---------------------------------------------------------------------------
  // store_locator — PDP find-in-store drawer OPEN. The locator itself is a
  // cross-origin widget (Locally / Outtra iframe), so the searched location
  // can't be read from the parent page — the only reliable signal is the
  // first interaction that opens the drawer, which is what this tracks.
  // Triggers: [data-find-in-store] (sidebar CTA / buy-buttons) and
  // [data-open-find-in-store] (find_in_store PDP block) — see
  // snippets/modal-find-in-store-drawer.liquid. Fires ONCE per page load
  // ("first interaction"); capture phase so drawer handlers can't swallow it.
  // Payload mirrors the other PDP events (GA4 ecommerce items = the product
  // that prompted the lookup).
  // ---------------------------------------------------------------------------
  guard(() => {
    let fired = false;
    document.addEventListener('click', (event) => {
      if (fired) return;
      if (!event.target.closest('[data-find-in-store], [data-open-find-in-store]')) return;
      fired = true;
      // Spec shape (brief §4.7) is { event, location } — the searched location.
      // The search itself happens inside the Outtra widget's cross-origin iframe
      // (brandwidgets.outtra.com, snippets/modal-store-locator-drawer.liquid), so
      // the term is unreadable from here: `location` is emitted empty unless a
      // theme-side input holds it. The ecommerce items stay attached as extra
      // context — the PDP product that prompted the lookup — but the spec fields
      // come first so a GTM variable on `location` always resolves.
      let searched = '';
      const input = document.querySelector(
        '[data-store-locator-input], .store-locator-drawer input[type="search"], .store-locator-drawer input[type="text"]'
      );
      if (input && input.value) searched = String(input.value).trim();
      const ec = currentEcom();
      if (ec) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({ event: 'store_locator', location: searched, ecommerce: ec });
      } else {
        dl({ event: 'store_locator', location: searched });
      }
    }, true);
  });


  // ---------------------------------------------------------------------------
  // Newsletter — newsletter_subscription_started (on submit)
  // (newsletter_subscription_completed is pushed from Liquid on success render)
  // ---------------------------------------------------------------------------
  guard(() => {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const isNewsletter =
        form.id === 'ContactFooter' ||
        form.classList.contains('newsletter-form') ||
        form.classList.contains('newsletter-modal__form') || // CRM signup modal (the live form)
        !!form.querySelector('input[name="contact[email]"]');
      if (isNewsletter) {
        // Fire once per form — the modal re-submits on every attempt,
        // including ones its own validation rejects.
        if (form.dataset.dlNlStarted === '1') return;
        form.dataset.dlNlStarted = '1';
        dl({ event: 'newsletter_subscription_started' });
      }
    }, true);
  });

  // ---------------------------------------------------------------------------
  // login / register_account are intentionally not implemented: auth is an
  // external OAuth (Auth0) redirect, so no theme form submit ever fires.
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Video — video_played / video_completed
  //
  // Three capture paths:
  //   1. Native <video> — DOM play/ended events (capture phase).
  //   2. YouTube modal embeds (vwto, modal-video, athlete gallery, pdp-service)
  //      — those surfaces add enablejsapi=1 to the embed URL; a MutationObserver
  //      here attaches a YT.Player to each such iframe and listens for
  //      PLAYING/ENDED via the YouTube IFrame API.
  //   3. Shopify product-media external videos (deferred-media, theme.js) —
  //      reuse the YT/Vimeo playerController theme.js already creates, hooked
  //      via the bubbling `media:play` CustomEvent.
  // De-dup: video_played and video_completed each fire at most once per
  // video_id per page load (a click-based "played intent" and the later API
  // PLAYING state share the same registry, so no double-fire).
  // Not covered: Vimeo modal embeds (no Vimeo SDK loaded on those surfaces)
  // still get click-based video_played only; decorative muted background loops
  // are excluded everywhere.
  // ---------------------------------------------------------------------------
  guard(() => {
    // Normalize provider casing to the spec's canonical names (the markup uses
    // lower-case data-video-type, e.g. "youtube"; the spec expects "YouTube").
    const PROVIDER_NAMES = { youtube: 'YouTube', vimeo: 'Vimeo', shopify: 'Shopify' };
    const normProvider = (p) => {
      if (!p) return p;
      const key = String(p).toLowerCase();
      return PROVIDER_NAMES[key] || (p.charAt(0).toUpperCase() + p.slice(1));
    };

    // Once-per-page-per-video registries shared by all capture paths.
    const playedIds = new Set();
    const completedIds = new Set();
    const pushVideo = (event, registry, video) => {
      const key = video.video_id || video.title;
      if (key) {
        if (registry.has(key)) return;
        registry.add(key);
      }
      dl({ event, video });
    };
    const pushPlayed = (video) => pushVideo('video_played', playedIds, video);
    const pushCompleted = (video) => pushVideo('video_completed', completedIds, video);

    const meta = (el) => {
      const host = el.closest('[data-video-id], video-with-text-overlay') || el;
      const provider = host.getAttribute && host.getAttribute('data-video-type');
      return {
        title: (host.getAttribute && (host.getAttribute('data-video-title') || host.getAttribute('aria-label'))) || document.title,
        provider: normProvider(provider || (el.tagName === 'VIDEO' ? 'Shopify' : '')),
        video_id: (host.getAttribute && host.getAttribute('data-video-id')) || '',
        duration: el.duration && isFinite(el.duration) ? Math.round(el.duration) : null,
      };
    };

    // -- 1. Native <video> elements (capture phase: play/ended don't bubble).
    // Skip muted autoplay loops — those are decorative background videos, not a
    // user-initiated "play", and would otherwise fire on every page load.
    const isBackground = (v) => v.muted && (v.autoplay || v.loop);
    document.addEventListener('play', (event) => {
      const v = event.target;
      if (v && v.tagName === 'VIDEO' && !isBackground(v)) pushPlayed(meta(v));
    }, true);
    document.addEventListener('ended', (event) => {
      const v = event.target;
      if (v && v.tagName === 'VIDEO' && !isBackground(v)) pushCompleted(meta(v));
    }, true);

    // Click-based "played intent" for embeds opened via a play button. For
    // YouTube the IFrame API path below would fire the same video_played a
    // moment later — the shared registry de-dups it. This click remains the
    // ONLY video_played source for Vimeo modal embeds.
    document.addEventListener('click', (event) => {
      const btn = event.target.closest('.vwto__play-btn, [data-video-id] [data-play], [data-video-play]');
      if (!btn) return;
      const host = btn.closest('[data-video-id], video-with-text-overlay');
      if (!host) return;
      pushPlayed({
        title: host.getAttribute('data-video-title') || txt(host.querySelector('h2, h3, .vwto__title')) || document.title,
        provider: normProvider(host.getAttribute('data-video-type') || ''),
        video_id: host.getAttribute('data-video-id') || '',
        duration: null,
      });
    });

    // -- Shared YouTube IFrame API helpers --------------------------------
    const loadYTApi = () => {
      if (window.YT && window.YT.Player) return Promise.resolve();
      if (!window.__dlYTApiPromise) {
        window.__dlYTApiPromise = new Promise((resolve) => {
          const prev = window.onYouTubeIframeAPIReady;
          window.onYouTubeIframeAPIReady = () => {
            if (typeof prev === 'function') prev();
            resolve();
          };
          if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
            const s = document.createElement('script');
            s.src = 'https://www.youtube.com/iframe_api';
            document.head.appendChild(s);
          }
        });
      }
      return window.__dlYTApiPromise;
    };

    const ytMeta = (player, iframe) => {
      let data = {};
      let dur = null;
      try { data = player.getVideoData() || {}; } catch (e) {}
      try { dur = player.getDuration ? Math.round(player.getDuration()) : null; } catch (e) {}
      const host = iframe && iframe.closest ? iframe.closest('[data-video-title], [data-video-id], video-with-text-overlay') : null;
      const srcId = iframe && iframe.src ? (iframe.src.match(/\/embed\/([\w-]{11})/) || [])[1] : '';
      return {
        title: data.title || (host && host.getAttribute('data-video-title')) || document.title,
        provider: 'YouTube',
        video_id: data.video_id || srcId || '',
        duration: dur && isFinite(dur) && dur > 0 ? dur : null,
      };
    };

    const ytStateHandler = (getPlayer, iframe) => (ev) => guard(() => {
      const player = getPlayer();
      if (!player || !window.YT) return;
      if (ev.data === window.YT.PlayerState.PLAYING) pushPlayed(ytMeta(player, iframe));
      else if (ev.data === window.YT.PlayerState.ENDED) pushCompleted(ytMeta(player, iframe));
    });

    // -- 2. YouTube modal embeds — observe iframes that opt in via
    // enablejsapi=1 (added by the modal surfaces). autoplay=1 + no mute=1
    // filters out both decorative background loops and the hidden
    // duration-probe player; deferred-media is handled by path 3.
    const attachedSrc = new WeakMap(); // iframe -> src a player was attached for
    const isTrackableYT = (iframe) => {
      const src = iframe.src || '';
      return src.indexOf('https://www.youtube.com/embed/') === 0 &&
        src.indexOf('enablejsapi=1') > -1 &&
        src.indexOf('autoplay=1') > -1 &&
        src.indexOf('mute=1') === -1 &&
        !iframe.closest('deferred-media');
    };
    const attachIframe = (iframe) => guard(() => {
      if (iframe.tagName !== 'IFRAME' || !isTrackableYT(iframe)) return;
      if (attachedSrc.get(iframe) === iframe.src) return;
      attachedSrc.set(iframe, iframe.src);
      loadYTApi().then(() => guard(() => {
        let player = null;
        player = new window.YT.Player(iframe, {
          events: { onStateChange: ytStateHandler(() => player, iframe) },
        });
      }));
    });
    const scanForIframes = (root) => guard(() => {
      if (root.tagName === 'IFRAME') attachIframe(root);
      if (root.querySelectorAll) root.querySelectorAll('iframe').forEach(attachIframe);
    });
    scanForIframes(document.documentElement);
    new MutationObserver((muts) => {
      muts.forEach((m) => {
        if (m.type === 'attributes') attachIframe(m.target);
        else m.addedNodes.forEach((n) => { if (n.nodeType === 1) scanForIframes(n); });
      });
    }).observe(document.documentElement, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['src'],
    });

    // -- 3. Shopify product-media external videos (deferred-media). theme.js
    // already builds a YT/Vimeo playerController and dispatches a bubbling
    // `media:play` CustomEvent (detail = the deferred-media element) — reuse
    // that controller instead of wrapping the iframe a second time.
    const dmAttached = new WeakSet();
    document.addEventListener('media:play', (e) => guard(() => {
      const el = e.detail;
      if (!el || dmAttached.has(el) || !el.playerController) return;
      // Policy: only user-initiated playback is tracked. An autoplaying
      // deferred-media (none exist today) is ambient/background — skip it.
      if (el.hasAttribute && el.hasAttribute('autoplay')) return;
      const iframe = el.querySelector ? el.querySelector('iframe') : null;
      if (el.mediaKind === 'youtube') {
        dmAttached.add(el);
        const player = el.playerController;
        pushPlayed(ytMeta(player, iframe)); // this media:play IS the first play
        if (typeof player.addEventListener === 'function') {
          player.addEventListener('onStateChange', ytStateHandler(() => player, iframe));
        }
      } else if (el.mediaKind === 'vimeo') {
        dmAttached.add(el);
        const player = el.playerController;
        const vid = iframe && iframe.src ? (iframe.src.match(/vimeo\.com\/video\/(\d+)/) || [])[1] || '' : '';
        const vimeoMeta = () => Promise.all([
          player.getVideoTitle ? player.getVideoTitle().catch(() => null) : Promise.resolve(null),
          player.getDuration ? player.getDuration().catch(() => null) : Promise.resolve(null),
        ]).then(([title, dur]) => ({
          title: title || document.title,
          provider: 'Vimeo',
          video_id: vid,
          duration: dur ? Math.round(dur) : null,
        }));
        vimeoMeta().then((v) => guard(() => pushPlayed(v)));
        if (typeof player.on === 'function') {
          player.on('ended', () => guard(() => { vimeoMeta().then((v) => guard(() => pushCompleted(v))); }));
        }
      }
    }));
  });

  // ---------------------------------------------------------------------------
  // view_cart — fire when the user opens the native cart drawer. Shopify's
  // cart_viewed does not fire for the drawer (only a /cart page view would).
  // ---------------------------------------------------------------------------
  guard(() => {
    let lastViewCart = 0;
    // The /cart PAGE also exists (e.g. /de-at/cart). Shopify's own cart_viewed is
    // commented out in the pixel, and the click handler below only covers the
    // drawer, so a direct visit to /cart would emit nothing. Fire once on load.
    const isCartPage = /\/cart\/?$/.test(window.location.pathname);
    if (isCartPage) {
      const fireCartPage = () => guard(() => {
        lastViewCart = Date.now();
        fetchCartJson()
          .then((cart) => { if (cart && cart.item_count > 0) dlEcom('view_cart', cartToEcom(cart)); })
          .catch(() => {});
      });
      if (document.readyState === 'complete' || document.readyState === 'interactive') {
        setTimeout(fireCartPage, 0);
      } else {
        document.addEventListener('DOMContentLoaded', () => setTimeout(fireCartPage, 0));
      }
    }
    document.addEventListener('click', (event) => {
      const opener = event.target.closest(
        '#cart-icon-bubble, [id^="cart-icon-bubble"], [class*="header__icon--cart"]'
      );
      if (!opener) return;
      const now = Date.now();
      if (now - lastViewCart < 800) return; // de-dup duplicate icons / bubbling
      lastViewCart = now;
      guard(() => {
        fetchCartJson()
          .then((cart) => { if (cart && cart.item_count > 0) dlEcom('view_cart', cartToEcom(cart)); })
          .catch(() => {});
      });
    });
  });

  // ---------------------------------------------------------------------------
  // remove_from_cart AND add_to_cart — both derived from one cart diff, because
  // adds and removals happen through many paths (PDP form, quick-add, ski sets,
  // cart drawer, cart page). Watching the cart catches all of them without
  // hooking every form.
  //
  // add_to_cart moved here from the Custom Pixel (AAS — data-layer brief): the
  // pixel runs in a sandboxed iframe with no access to metafields, so its items
  // could never carry item_brand (franchise) or item_category2..8. The
  // `product_added_to_cart` case in docs/custom-pixel.js is commented out — both
  // must never be live at once or GA4 double-counts every add.
  // ---------------------------------------------------------------------------
  guard(() => {
    let snap = {}; // line key -> { li, q }
    let seeded = false; // don't report the initial cart contents as adds
    // Keyed by VARIANT id, quantities aggregated — NOT by li.key. Shopify's line
    // key is `variantId:hash(properties)`, so it changes whenever line properties
    // change or two lines merge. With key-based snapshots a single quantity change
    // on a re-keyed line looked like "old line removed, new line added" and emitted
    // remove_from_cart + add_to_cart for one user action. Variant keying is stable
    // across property churn; the trade-off is that two lines of the same variant
    // with different properties (e.g. a gift message) are counted as one, which is
    // what GA4 wants anyway.
    const buildSnap = (cart) => {
      const m = {};
      (cart.items || []).forEach((li) => {
        const k = String(li.variant_id || li.key);
        if (m[k]) {
          m[k].q += li.quantity;
        } else {
          m[k] = { li: li, q: li.quantity };
        }
      });
      return m;
    };
    // Diffs MUST run one at a time. Several triggers can fire in quick succession
    // (cartUpdate + the fetch hook, or an add immediately followed by a quantity
    // change); concurrent runs each compared against the same stale snapshot and
    // then overwrote it, producing wrong quantities — e.g. a removal of 1 reported
    // as 2. Chaining on a single promise serialises them.
    let diffChain = Promise.resolve();
    const checkCartDiff = () => guard(() => {
      diffChain = diffChain.then(() => runCartDiff()).catch(() => {});
    });
    // NOT wrapped in guard(): guard swallows the return value, and the chain above
    // needs the promise to serialise on.
    const runCartDiff = () => {
      return fetchCartJson().then((cart) => {
        const next = buildSnap(cart);
        const removed = [];
        const added = [];
        Object.keys(snap).forEach((key) => {
          const oldQ = snap[key].q;
          const newQ = next[key] ? next[key].q : 0;
          if (newQ < oldQ) {
            // Enriched, not bare: removals need the same PIM fields as adds, or
            // remove_from_cart reports vendor-as-brand and no categories.
            const item = enrichedItemFromCartLine(snap[key].li, cart);
            item.quantity = oldQ - newQ;
            removed.push(item);
          }
        });
        if (seeded) {
          Object.keys(next).forEach((key) => {
            const oldQ = snap[key] ? snap[key].q : 0;
            const newQ = next[key].q;
            if (newQ > oldQ) {
              const item = enrichedItemFromCartLine(next[key].li, cart);
              item.quantity = newQ - oldQ;
              added.push(item);
            }
          });
        }
        if (removed.length) {
          const value = removed.reduce((s, i) => s + (i.price || 0) * (i.quantity || 0), 0);
          dlEcom('remove_from_cart', { currency: cart.currency, value: value, items: removed });
        }
        if (added.length) {
          const value = added.reduce((s, i) => s + (i.price || 0) * (i.quantity || 0), 0);
          dlEcom('add_to_cart', { currency: cart.currency, value: value, items: added });
        }
        snap = next;
      }).catch(() => {});
    };
    const checkRemovals = checkCartDiff; // kept: existing call sites below
    // Seed: the cart may already hold items from a previous session — those are
    // not adds, so nothing is emitted until the first snapshot exists.
    fetchCartJson().then((cart) => { snap = buildSnap(cart); seeded = true; })
      .catch(() => { seeded = true; });
    // theme cart (cart page / quantity changes)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      guard(() => subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(checkRemovals, 60)));
    }
    // app-agnostic: any fetch to a Shopify cart-mutation endpoint
    guard(() => {
      if (window.__dlCartFetchHook || !window.fetch) return;
      window.__dlCartFetchHook = true;
      const of = window.fetch;
      // Include /cart/add so the snapshot stays current after adds (adds never
      // fire remove_from_cart — only quantity DECREASES do).
      const CART_MUTATIONS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
      const isMutation = (u) => typeof u === 'string' && CART_MUTATIONS.some((p) => u.includes(p));
      window.fetch = function (url) {
        const u = typeof url === 'string' ? url : url && url.url;
        const p = of.apply(this, arguments);
        if (isMutation(u)) { try { p.then(() => setTimeout(checkRemovals, 90)); } catch (e) {} }
        return p;
      };
    });
  });

  // ---------------------------------------------------------------------------
  // select_item — click on a product card in a list. Item comes from
  // data-gtm-item on the card link (snippets/card-product.liquid).
  // ---------------------------------------------------------------------------
  guard(() => {
    const onSelect = (event) => {
      // Opening the card in a new tab is still a selection, and a middle-click
      // fires auxclick — NOT click — so binding click alone silently dropped
      // every "open in new tab". auxclick also fires for the RIGHT button, which
      // only opens a context menu and may never lead anywhere, so restrict to
      // the middle button (1). Same treatment as assets/breadcrumb-context.js.
      if (event.type === 'auxclick' && event.button !== 1) return;
      // skip add-to-cart / quick-buy inside the card (that's add_to_cart)
      if (event.target.closest('[name="add"], product-form, quick-add, .quick-add, [class*="quick-add"], [class*="quick-buy"]')) return;
      const card = event.target.closest('[data-gtm-item]');
      if (!card) return;
      let item;
      try { item = JSON.parse(card.getAttribute('data-gtm-item')); } catch (e) { return; }
      if (!item) return;
      const listRoot = listRootOf(card);
      const info = listInfo(listRoot);
      const listName = info.listName;
      const listId = info.listId;
      // index: position of the card WITHIN ITS OWN LIST (0-based). Scoped to the
      // nearest list root so a page holding more than one list (PDP related +
      // recently-viewed, or the search overlay open above a collection grid)
      // doesn't continue one list's numbering into the next. Falls back to the
      // document for grids that don't declare a list root yet.
      // Compare by card container, not by anchor — the clicked anchor may be the
      // title while uniqueCards kept the card's image link.
      const cards = uniqueCards((listRoot || document).querySelectorAll('[data-gtm-item]'))
        .map(dedupeKeyOf);
      const index = cards.indexOf(dedupeKeyOf(card));
      item.item_list_name = listName;
      if (listId) item.item_list_id = listId;
      if (index > -1) item.index = index;
      // Liquid renders the pre-discount price on cards too; take the painted
      // Regios sale price for this card when there is one.
      const cardPainted = cardPriceState(card);
      rememberPainted(productIdOfNode(card), cardPainted);
      Object.assign(item, cardPainted);
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined;
      const ecom = { currency: currency, item_list_name: listName, items: [item] };
      if (listId) ecom.item_list_id = listId;
      dlEcom('select_item', ecom);
    };
    // A plain left click fires only 'click'; a middle click fires only
    // 'auxclick'. Binding both cannot double-report the same interaction.
    document.addEventListener('click', onSelect);
    document.addEventListener('auxclick', onSelect);
  });

  // ---------------------------------------------------------------------------
  // APP-OWNED PRODUCT SURFACES (Wishlist King)
  //
  // The app injects its own markup, so Liquid can never put data-gtm-item on
  // those links. Client-side we only have the handle from the href, and
  // /products/<handle>.js does not expose metafields — the PIM fields would be
  // lost. So the payload is fetched over the Section Rendering API from
  // sections/datalayer-item-payload.liquid, which renders the SAME snippet the
  // theme's own cards use. An app surface therefore reports exactly what a
  // native card reports.
  //
  // Payloads are hydrated onto the links as data-gtm-item, which means the
  // existing select_item / view_item_list machinery picks them up unchanged —
  // no parallel code path, no second definition of "what an item is".
  // ---------------------------------------------------------------------------
  guard(() => {
    const APP_LINK = 'a.wk-image-link, a.wk-text-link';
    const APP_LIST = '.wk-grid';
    const cache = {};
    const inflight = {};

    const handleOf = (href) => {
      try {
        const path = new URL(href, location.origin).pathname;
        const m = path.match(/\/products\/([^/?#]+)/);
        return m ? m[1] : null;
      } catch (e) { return null; }
    };

    const fetchPayload = (handle) => {
      if (cache[handle]) return Promise.resolve(cache[handle]);
      if (inflight[handle]) return inflight[handle];
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = root.replace(/\/$/, '') + '/products/' + handle + '?section_id=datalayer-item-payload';
      inflight[handle] = fetch(url)
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          if (!html) return null;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const el = doc.querySelector('[data-dl-item-payload]');
          const raw = el ? el.textContent.trim() : '';
          if (!raw) return null;
          const parsed = JSON.parse(raw);
          cache[handle] = parsed;
          return parsed;
        })
        .catch(() => null)
        .then((v) => { delete inflight[handle]; return v; });
      return inflight[handle];
    };

    // Hydrate every app link that does not already carry a payload, then let the
    // normal collectors run. Bounded so a broken app can't loop forever.
    let hydrations = 0;
    const hydrate = () => guard(() => {
      // NB: ':not()' binds only to the LAST selector in a comma list, so the
      // filter is applied per-node instead of in the selector.
      const links = Array.prototype.filter.call(
        document.querySelectorAll(APP_LINK),
        (a) => !a.hasAttribute('data-gtm-item')
      );
      if (!links.length) return;
      if (hydrations > 60) return;
      hydrations += links.length;
      const jobs = [];
      links.forEach((a) => {
        const handle = handleOf(a.getAttribute('href'));
        if (!handle) return;
        jobs.push(fetchPayload(handle).then((payload) => {
          if (payload) a.setAttribute('data-gtm-item', JSON.stringify(payload));
        }));
      });
      if (!jobs.length) return;
      Promise.all(jobs).then(() => guard(() => {
        // Mark the app's grid as its own list so these never fall into the
        // page-level bucket, then let view_item_list pick it up.
        document.querySelectorAll(APP_LIST).forEach((grid) => {
          if (!grid.hasAttribute('data-list-id')) {
            grid.setAttribute('data-list-id', 'wishlist');
            grid.setAttribute('data-list-name', 'Wishlist');
          }
        });
        window.dispatchEvent(new Event('scroll'));
      }));
    });

    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      setTimeout(hydrate, 0);
    } else {
      document.addEventListener('DOMContentLoaded', () => setTimeout(hydrate, 0));
    }
    // The app renders asynchronously and re-renders on add/remove.
    guard(() => {
      let pending = null;
      const obs = new MutationObserver(() => {
        if (pending) return;
        pending = setTimeout(() => { pending = null; hydrate(); }, 300);
      });
      obs.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => { try { obs.disconnect(); } catch (e) {} }, 20000);
    });
  });

  // ---------------------------------------------------------------------------
  // select_promotion (§2.13) — a promotional placement was clicked.
  //
  // Promotional surfaces that ALSO link to a product (hero slide, hotspot pin,
  // feature-gallery tile) carry BOTH data-gtm-item and data-gtm-promo and emit
  // BOTH events on one click. They are different questions — "which product did
  // they choose" vs "which creative sent them" — and GA4 models them separately.
  // A promo that links somewhere other than a product carries only the promo
  // payload and fires only this.
  // ---------------------------------------------------------------------------
  guard(() => {
    const onPromo = (event) => {
      if (event.type === 'auxclick' && event.button !== 1) return;
      if (!event.target || !event.target.closest) return;
      const el = event.target.closest('[data-gtm-promo]');
      if (!el) return;
      let promo;
      try { promo = JSON.parse(el.getAttribute('data-gtm-promo')); } catch (e) { return; }
      if (!promo) return;
      dlEcom('select_promotion', { items: [promo] });
    };
    document.addEventListener('click', onPromo);
    document.addEventListener('auxclick', onPromo);
  });

  // ---------------------------------------------------------------------------
  // view_promotion (§2.12) — a promotional placement was seen. Deduped per
  // promotion_id so a carousel returning to slide 1 doesn't re-report it.
  // ---------------------------------------------------------------------------
  guard(() => {
    const seen = new Set();
    const report = () => guard(() => {
      document.querySelectorAll('[data-gtm-promo]').forEach((el) => {
        let promo;
        try { promo = JSON.parse(el.getAttribute('data-gtm-promo')); } catch (e) { return; }
        if (!promo || !promo.promotion_id || seen.has(promo.promotion_id)) return;
        seen.add(promo.promotion_id);
        dlEcom('view_promotion', { items: [promo] });
      });
    });
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      setTimeout(report, 0);
    } else {
      document.addEventListener('DOMContentLoaded', () => setTimeout(report, 0));
    }
    // Promos can be injected late (slideshow clones, hotspot pins). Same bounded
    // re-check as view_item_list; dedupe by promotion_id keeps it idempotent.
    guard(() => {
      let pending = null;
      const obs = new MutationObserver(() => {
        if (pending) return;
        pending = setTimeout(() => { pending = null; report(); }, 200);
      });
      obs.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => { try { obs.disconnect(); } catch (e) {} }, 10000);
    });
  });

  // ---------------------------------------------------------------------------
  // add_to_wishlist — relayed from the theme's OWN wishlist toggle. The Wishlist
  // King app exposes no usable JS event, but every toggle is a
  // [data-wishlist-toggle][data-product-handle] button whose pressed-state flips
  // on click (the same signal assets/wishlist-modal.js relies on). Capture phase
  // so we read the state BEFORE it flips, and fire ONLY on the OFF→ON (add)
  // transition so removals never fire. Item resolution: product-card payload
  // (data-gtm-item) → PDP window.atomicProductDL → minimal id-only fallback.
  // ---------------------------------------------------------------------------
  guard(() => {
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-wishlist-toggle][data-product-handle]');
      if (!toggle) return;
      // This widget exposes neither is-wishlisted nor aria-pressed, and
      // re-renders the toggle after each click (wiping any DOM dataset flag),
      // so track on/off state in a module-level map keyed by product handle.
      window.__dlWishState = window.__dlWishState || {};
      const wishKey = toggle.getAttribute('data-product-handle') ||
        toggle.getAttribute('data-product-id') || 'unknown';
      const wasOn = toggle.classList.contains('is-wishlisted') ||
        toggle.getAttribute('aria-pressed') === 'true' ||
        window.__dlWishState[wishKey] === true;
      window.__dlWishState[wishKey] = !wasOn;
      if (wasOn) return; // removal — only an add fires add_to_wishlist

      let item = null;
      // 1) product-card payload (toggle sits beside a data-gtm-item link in the card)
      const card = toggle.closest('.card-product, .card-wrapper');
      const gtmEl = card && card.querySelector('[data-gtm-item]');
      if (gtmEl) {
        try { item = JSON.parse(gtmEl.getAttribute('data-gtm-item')); } catch (e) { item = null; }
      }
      // 2) PDP item exposed by snippets/datalayer-product-item.liquid
      if (!item && window.atomicProductDL && window.atomicProductDL.item) {
        item = window.atomicProductDL.item;
      }
      // 3) minimal fallback from the toggle's own data-* attributes
      if (!item) {
        const id = toggle.getAttribute('data-variant-id') ||
          toggle.getAttribute('data-product-id') ||
          toggle.getAttribute('data-product-handle') || '';
        if (!id) return;
        item = { item_id: id, quantity: 1 };
      }

      const currency =
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
        (window.atomicProductDL && window.atomicProductDL.currency) || undefined;
      const value = (typeof item.price === 'number') ? item.price
        : (typeof item.price === 'number') ? item.price : undefined;
      // De-dupe on the RESOLVED item, not the toggle: every card renders two
      // toggles for the same product (.product-card__wishlist-btn and
      // .card-product__mobile-actions-btn) and they expose different attributes —
      // one has data-product-id, the other doesn't. When the widget syncs the pair
      // by dispatching its own click, the second event resolves to a different
      // state key and slips past the OFF→ON check, firing add_to_wishlist twice
      // for one user action. Keying the guard on item_id closes that regardless of
      // which element or attribute the second event carries.
      const dedupeKey = String(item.item_id || wishKey);
      window.__dlWishLast = window.__dlWishLast || {};
      const now = Date.now();
      if (window.__dlWishLast[dedupeKey] && (now - window.__dlWishLast[dedupeKey]) < 2000) return;
      window.__dlWishLast[dedupeKey] = now;
      dlEcom('add_to_wishlist', { currency: currency, value: value, items: [item] });
    }, true);
  });

  // ---------------------------------------------------------------------------
  // Keep the hidden properties[_item_*] inputs in sync with the SELECTED variant.
  // snippets/datalayer-item-props.liquid renders them once from the variant Liquid
  // resolved at page render; the shopper then changes size and product-form.js
  // updates only the `id` input, so _item_variant / _item_category5 /
  // _item_category6 would be written to the cart line describing the WRONG variant
  // (observed: line "…/ 29/29.5" carrying _item_variant "27/27.5"). Product-scoped
  // props (brand, cat2/3/4/7/8) never change and are left alone.
  // ---------------------------------------------------------------------------
  guard(() => {
    if (!baseDL()) return; // PDP only
    const syncProps = () => guard(() => {
      const data = baseDL();
      if (!data || !Array.isArray(data.variants)) return;
      const idEl = document.querySelector(
        'product-info input[name="id"], product-info select[name="id"], form[id^="product-form-"] [name="id"]'
      );
      const vid = idEl ? Number(idEl.value) : null;
      if (!vid) return;
      const v = data.variants.find((x) => Number(x.id) === vid);
      if (!v) return;
      const set = (key, value) => {
        document.querySelectorAll('input[name="properties[' + key + ']"]').forEach((input) => {
          if (value == null || value === '') { input.value = ''; return; }
          input.value = value;
        });
      };
      set('_item_variant', v.sizeCode || '');
      set('_item_category5', v.discountState || '');
      set('_item_category6', v.color || '');
    });
    // Run after the picker settles, then on every variant change the theme emits.
    setTimeout(syncProps, 0);
    document.addEventListener('change', (e) => {
      if (e.target && e.target.closest && e.target.closest('variant-radios, variant-selects, product-info, form[id^="product-form-"]')) {
        setTimeout(syncProps, 60);
      }
    });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      guard(() => subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(syncProps, 60)));
    }
  });

  // ---------------------------------------------------------------------------
  // view_item — PDP view. Moved here from the Custom Pixel: the pixel sandbox
  // cannot read metafields, so its view_item could never carry item_brand
  // (franchise) or item_category2..8. The `product_viewed` case in
  // docs/custom-pixel.js is commented out — never run both.
  //
  // Fires once per page, after the variant picker has settled, so a ?variant=
  // deep link reports the deep-linked variant rather than the default.
  // ---------------------------------------------------------------------------
  // item_category5 on the PDP: compare_at_price only sees Shopify markdowns.
  // Regios automatic discounts are computed by the app at runtime and never
  // touch compare_at_price, so Liquid reports "full_price" on a discounted
  // product. Rather than re-implementing the app's matching rules (collections,
  // tags, quantity tiers, customer segments — all in RegiosDOPP.discountConfigs)
  // or paying for an extra API round-trip, watch for the paint: both the app
  // embed and assets/regios-dopp-sync.js mark a discounted price with
  // RegiosDOPP.priceOnSaleClass (default 'price--on-sale') on the price root.
  // Resolves as soon as the class appears, else gives up after the timeout.
  const REGIOS_WAIT_MS = 1200;
  const regiosSaleSelector = () => {
    const cls = (window.RegiosDOPP && window.RegiosDOPP.priceOnSaleClass) || 'price--on-sale';
    return '.' + String(cls).trim().split(/\s+/).join('.') + ', .regios-dopp-generic-price--on-sale';
  };
  // Read the rendered price block. The container and both price items are
  // server-rendered hooks (snippets/price.liquid, price-max.liquid), so they are
  // always present — what changes is whether the strike-through REGULAR cell ends
  // up holding a different price from the SALE cell. That text comparison is more
  // reliable than the on-sale class: our own sync script adds the class
  // (regios-dopp-sync.js:174) but the app embed's paint may not. Same approach
  // snippets/ski-set.liquid:207 already uses.
  // Scope: the PDP's OWN price block only — sections/main-product.liquid renders
  // it as #price-<sectionId> (.product__price). A PDP also renders recommendation
  // and complementary CARDS, each with its own price container, so a page-wide
  // query would flag the main product as discounted (and could even read another
  // product's price) whenever any card on the page is on sale.
  const pdpPriceRoot = () => document.querySelector('[id^="price-"].product__price')
    || document.querySelector('.product__price');
  const domSaysDiscounted = () => {
    const root = pdpPriceRoot();
    if (!root) return false;
    if (root.matches(regiosSaleSelector()) || root.querySelector(regiosSaleSelector())) return true;
    if (root.closest('.price') && root.closest('.price').matches(regiosSaleSelector())) return true;
    const containers = root.querySelectorAll('.regios-dopp-generic-price-container');
    for (let i = 0; i < containers.length; i += 1) {
      const reg = txt(containers[i].querySelector('.regios-dopp-generic-price-item--regular'));
      const sale = txt(containers[i].querySelector('.regios-dopp-generic-price-item--sale'));
      if (reg && sale && reg !== sale) return true;
    }
    return false;
  };
  // Parse a rendered money string ("€ 462,00", "$1,299.00", "1 299,00 €") into a
  // number. Locale-agnostic: whichever of , or . comes LAST is the decimal
  // separator when it is followed by 1-2 digits; anything else is a grouping mark.
  const parseMoney = (str) => {
    if (!str) return null;
    const cleaned = String(str).replace(/[^\d.,-]/g, '');
    if (!cleaned) return null;
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    const sepIdx = Math.max(lastComma, lastDot);
    let normalised;
    if (sepIdx > -1 && /^\d{1,2}$/.test(cleaned.slice(sepIdx + 1))) {
      normalised = cleaned.slice(0, sepIdx).replace(/[.,]/g, '') + '.' + cleaned.slice(sepIdx + 1);
    } else {
      normalised = cleaned.replace(/[.,]/g, '');
    }
    const n = Number(normalised);
    return Number.isFinite(n) ? n : null;
  };

  // The price the shopper actually pays, read from the painted SALE cell. Liquid
  // only knows variant.price (pre-discount), so without this price and value
  // overstate revenue on every Regios-discounted product.
  const domSalePrice = () => {
    const root = pdpPriceRoot();
    if (!root) return null;
    const containers = root.querySelectorAll('.regios-dopp-generic-price-container');
    for (let i = 0; i < containers.length; i += 1) {
      const reg = txt(containers[i].querySelector('.regios-dopp-generic-price-item--regular'));
      const sale = txt(containers[i].querySelector('.regios-dopp-generic-price-item--sale'));
      if (reg && sale && reg !== sale) {
        const salePrice = parseMoney(sale);
        const regPrice = parseMoney(reg);
        // Only trust it when it really is lower AND both are positive. The price
        // block also holds the unit-price line ("Stückpreis € 0,00"), and the theme
        // re-renders #price-<section> on cart/variant updates, so a mid-render read
        // could otherwise yield 0 and wipe price.
        if (salePrice != null && regPrice != null
            && salePrice > 0 && regPrice > 0 && salePrice < regPrice) return salePrice;
      }
    }
    return null;
  };

  // Same read, scoped to one product CARD — used by select_item and
  // view_item_list, whose Liquid payloads also carry the pre-discount price.
  // Cards carry data-regios-dopp-generic-product-id (see
  // snippets/regios-dopp-sync.liquid), which is the most reliable card root.
  const cardPriceState = (el) => {
    const root = el.closest('[data-regios-dopp-generic-product-id]')
      || el.closest('li, article, .card-wrapper')
      || el.parentElement;
    if (!root) return {};
    const cont = root.querySelector('.regios-dopp-generic-price-container');
    if (!cont) return {};
    const reg = txt(cont.querySelector('.regios-dopp-generic-price-item--regular'));
    const sale = txt(cont.querySelector('.regios-dopp-generic-price-item--sale'));
    if (!reg || !sale || reg === sale) return {};
    const salePrice = parseMoney(sale);
    const regPrice = parseMoney(reg);
    if (salePrice == null || regPrice == null) return {};
    if (salePrice <= 0 || regPrice <= 0 || salePrice >= regPrice) return {};
    return { price: salePrice, item_category5: 'discounted' };
  };

  // ---------------------------------------------------------------------------
  // List attribution — shared by select_item and view_item_list so the two agree
  // and join up in GA4 list reports. A list declares itself by putting
  // data-list-id / data-list-name on any ancestor of its cards. Grids that don't
  // declare one yet fall back to the page-level chain (collection handle from
  // [data-collection] or the URL; heading, else the document title with its
  // " – Site Name" suffix trimmed) — which is what a PLP has always used.
  // ---------------------------------------------------------------------------
  const listRootOf = (el) => el.closest('[data-list-id], [data-list-name]');

  // A card exposes the payload on EVERY product link it has — image and title
  // both — so that a click anywhere meaningful resolves. For counting and for
  // view_item_list that must collapse back to one entry per card, or a two-link
  // card is reported twice and index numbering doubles. The card container is
  // [data-regios-dopp-generic-product-id], present on every card type.
  const cardKeyOf = (el) => el.closest('[data-regios-dopp-generic-product-id]') || el;
  // Dedupe key, most specific first:
  //   1. the card container element
  //   2. the payload's item_id — REQUIRED for surfaces with no card container.
  //      Cart line items are the case in point: image link and title link have
  //      no [data-regios-dopp-generic-product-id] wrapper, so keying on the
  //      element alone counted one cart line as two products and put duplicate
  //      item_ids into view_item_list.
  //   3. the element itself
  const dedupeKeyOf = (el) => {
    let container = el.closest('[data-regios-dopp-generic-product-id]');
    // A container that sits ABOVE this card's own list root is not this card's
    // container — it is a bigger thing that happens to enclose the list. The PDP
    // is the case in point: sections/main-product.liquid puts the Regios id on
    // the whole .product__info-container, which encloses the ski-set / system-set
    // widgets. Keying on it collapsed EVERY component in those lists to one key,
    // so view_item_list reported a single item and select_item's index was always
    // 0. Falling through to item_id below gives each component its own key.
    // Grid/cart surfaces are unaffected: there the container is INSIDE the list
    // root, so this guard never trips.
    if (container) {
      const root = listRootOf(el);
      if (root && !root.contains(container)) container = null;
    }
    if (container) return container;
    const raw = el.getAttribute('data-gtm-item');
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.item_id != null) return 'id:' + parsed.item_id;
      } catch (e) { /* fall through */ }
    }
    return el;
  };
  const uniqueCards = (nodes) => {
    const seen = new Set();
    const out = [];
    Array.prototype.forEach.call(nodes, (n) => {
      // Carousel clones are duplicate DOM for the same card — never report them.
      if (n.closest('.splide__slide--clone')) return;
      // Same reasoning for responsive duplicates: sections/main-product.liquid
      // renders the ski-set / system-set widgets TWICE (--desktop and --mobile),
      // one of which is always display:none, and system-set renders every
      // variant's row with the inactive ones `hidden`. Without this the hidden
      // copy could win the dedupe and its list — never on screen — would defer
      // for ever, so the VISIBLE one never reported. A card that is merely below
      // the fold still has rects, so this only drops genuinely hidden DOM; lists
      // revealed later are picked up by the re-collect passes.
      if (!n.getClientRects().length) return;
      const key = dedupeKeyOf(n);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(n);
    });
    return out;
  };
  const listInfo = (root) => {
    let listId = root ? (root.getAttribute('data-list-id') || '') : '';
    let listName = root ? (root.getAttribute('data-list-name') || '') : '';
    if (!listId) {
      const collEl = document.querySelector('[data-collection]');
      listId = (collEl && collEl.getAttribute('data-collection')) || '';
    }
    if (!listId) {
      const m = window.location.pathname.match(/\/collections\/([^/?#]+)/);
      if (m) listId = 'collection_' + m[1];
    }
    if (!listName) {
      const heading = document.querySelector(
        '.collection-hero__title, .collection__title, [class*="collection"] h1, main h1'
      );
      if (heading) listName = txt(heading);
    }
    if (!listName) {
      const title = document.title || '';
      let cut = -1;
      [' – ', ' | ', ' - '].forEach((sep) => { cut = Math.max(cut, title.lastIndexOf(sep)); });
      listName = (cut > -1 ? title.slice(0, cut) : title).trim();
    }
    return { listId: listId || undefined, listName: listName };
  };

  const waitForDiscountState = () => new Promise((resolve) => {
    if (domSaysDiscounted()) { resolve('discounted'); return; }
    // No app on the page → nothing will repaint, Liquid's answer stands.
    if (!window.RegiosDOPP) { resolve(null); return; }
    let done = false;
    const finish = (val) => { if (done) return; done = true; try { obs.disconnect(); } catch (e) {} resolve(val); };
    // Watch class flips AND text nodes — the embed repaints cell contents without
    // necessarily touching any class.
    const obs = new MutationObserver(() => { if (domSaysDiscounted()) finish('discounted'); });
    obs.observe(document.body, {
      subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ['class'],
    });
    setTimeout(() => finish(null), REGIOS_WAIT_MS);
  });

  guard(() => {
    if (!baseDL()) return; // not a PDP
    let fired = false;
    const fire = () => guard(() => {
      if (fired) return;
      const ec = currentEcom();
      if (!ec) return;
      fired = true;
      // Hold view_item until the app has had a chance to paint, so
      // item_category5 reflects automatic discounts too. Never blocks longer
      // than REGIOS_WAIT_MS, and never blocks at all without the app.
      waitForDiscountState().then((state) => {
        if (state && ec.items && ec.items[0]) {
          ec.items[0].item_category5 = state;
          // Report what the shopper actually pays, not variant.price.
          const sale = domSalePrice();
          if (sale != null) {
            ec.items[0].price = sale;
            ec.value = sale;
          }
        }
        dlEcom('view_item', ec);
      }).catch(() => dlEcom('view_item', ec));
    });
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      setTimeout(fire, 0);
    } else {
      document.addEventListener('DOMContentLoaded', () => setTimeout(fire, 0));
    }
  });

  // ---------------------------------------------------------------------------
  // view_item_list — product list view (collection / search / any grid of cards).
  // Items come from the same data-gtm-item payloads select_item reads, so the
  // list carries the full PIM payload. Moved here from the pixel for the same
  // reason as view_item; `collection_viewed` there is commented out.
  //
  // Only cards present at first paint are reported — pagination / infinite scroll
  // would need a re-fire per batch, which the brief does not ask for.
  // ---------------------------------------------------------------------------
  guard(() => {
    // Wait for the Regios paint before reading card prices, same as view_item —
    // otherwise every card reports the pre-discount Liquid price.
    const reportedRoots = new Set();

    // Viewport gating. A list counts as viewed once any of its cards is at least
    // partly on screen. Carousels are a deliberate exception: only the active
    // slide is on screen, but the whole carousel is "the list the shopper saw",
    // so the root's own box is what gets tested there.
    const isOnScreen = (nodes) => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      return Array.prototype.some.call(nodes, (n) => {
        const box = (n.closest('[data-list-id], [data-list-name]') || n).getBoundingClientRect();
        if (!box.width && !box.height) return false;
        return box.bottom > 0 && box.top < vh && box.right > 0 && box.left < vw;
      });
    };

    let viewObserver = null;
    let scrollBound = false;
    // Scroll/resize fallback. IntersectionObserver is the primary signal, but it
    // is not guaranteed: it is absent on old browsers and its callbacks are not
    // delivered while rendering is throttled (background tabs). Without this,
    // a deferred list would never be reported at all — worse than reporting it
    // early. Both paths funnel into the same deduped collect().
    const bindScrollFallback = () => {
      if (scrollBound) return;
      scrollBound = true;
      let pending = null;
      const onScroll = () => {
        if (pending) return;
        pending = setTimeout(() => { pending = null; collect(); }, 250);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      // Third net: a bounded poll. IntersectionObserver and scroll are both
      // tied to the rendering pipeline, and both go silent when it is throttled.
      // view_item_list is the event we can least afford to lose, so a plain
      // timer re-checks for a while. It still honours isOnScreen(), so this
      // reports lists the shopper actually reached — never the whole page.
      let ticks = 0;
      const poll = setInterval(() => {
        ticks += 1;
        if (ticks > 20) { clearInterval(poll); return; }
        collect();
      }, 1000);
    };
    const observeForView = (node) => {
      bindScrollFallback();
      if (typeof IntersectionObserver !== 'function') return;
      if (!viewObserver) {
        viewObserver = new IntersectionObserver((entries) => {
          let hit = false;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            hit = true;
            try { viewObserver.unobserve(entry.target); } catch (e) { /* noop */ }
          });
          if (hit) collect();
        }, { threshold: 0 });
      }
      try { viewObserver.observe(node); } catch (e) { /* noop */ }
    };
    const fire = () => guard(() => { waitForDiscountState().then(collect).catch(collect); });
    const collect = () => guard(() => {
      const nodes = document.querySelectorAll('[data-gtm-item]');
      if (!nodes.length) return;
      // Group the cards by the list they belong to and emit ONE event per list.
      // A page can hold several (a PDP's related products + recently-viewed;
      // the search overlay above a collection grid), and the brief requires the
      // PDP case explicitly: "Includes related products on item pages" (§2.10).
      // Cards in a grid that declares no list root group together under the
      // page-level fallback, which is what a plain PLP is.
      const groups = new Map();
      uniqueCards(nodes).forEach((node) => {
        const root = listRootOf(node) || null;
        if (!groups.has(root)) groups.set(root, []);
        groups.get(root).push(node);
      });
      groups.forEach((groupNodes, root) => {
        if (reportedRoots.has(root)) return;
        // "view_item_list" means the list was VIEWED. Emitting for every list in
        // the document reported lists the shopper never scrolled to — on a long
        // homepage that is most of them, and it inflates impressions while
        // destroying list CTR. Defer anything not yet on screen to the
        // IntersectionObserver below.
        if (!isOnScreen(groupNodes)) {
          groupNodes.forEach((n) => observeForView(n));
          return;
        }
        const info = listInfo(root);
        const items = [];
        for (let i = 0; i < groupNodes.length; i += 1) {
          try {
            const parsed = JSON.parse(groupNodes[i].getAttribute('data-gtm-item'));
            if (!parsed) continue;
            const painted = cardPriceState(groupNodes[i]);
            rememberPainted(productIdOfNode(groupNodes[i]), painted);
            items.push(Object.assign({}, parsed, {
              index: items.length,
              item_list_id: info.listId,
              item_list_name: info.listName,
            }, painted));
          } catch (e) { /* malformed attr — skip */ }
        }
        // Only mark the list reported once it actually emitted, so a container
        // that is still empty at first paint is picked up on a later pass.
        if (!items.length) return;
        reportedRoots.add(root);
        dlEcom('view_item_list', {
          item_list_id: info.listId,
          item_list_name: info.listName,
          items: items,
        });
      });
    });
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      setTimeout(fire, 0);
    } else {
      document.addEventListener('DOMContentLoaded', () => setTimeout(fire, 0));
    }

    // Lists that arrive after first paint: <product-recommendations> fetches its
    // cards from ?section_id=… , so a PDP's related-products list is usually not
    // in the DOM when the pass above runs. Re-collect on DOM growth (deduped by
    // reportedRoots) for a bounded window, then stop — this is not meant to
    // catch pagination or infinite scroll, which the brief does not ask for.
    guard(() => {
      let pending = null;
      const obs = new MutationObserver(() => {
        if (pending) return;
        pending = setTimeout(() => { pending = null; fire(); }, 200);
      });
      obs.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => { try { obs.disconnect(); } catch (e) {} }, 10000);
    });
  });
})();
